/**
 * Service layer — Spec Sections, Versions, and Submittal Register (Supabase-backed).
 *
 * Wraps /api/spec-sections. Spec sections live in the Construction branch under
 * a new "Spec Sections" subsection. Each section has a version chain (conformed
 * spec PDF + changes_summary for addendum view) and a submittal register (expected
 * submittal items the GC must deliver).
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/spec-sections' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    const err = new Error(b.error || 'Spec sections request failed: ' + res.status)
    err.status = res.status
    err.body = b
    if (b.release) err.release = b.release
    throw err
  }
  return res.json()
}

export const SPEC_SECTION_STATUS_LABELS = { draft: 'Draft', issued: 'Issued', superseded: 'Superseded' }
export const REGISTER_ITEM_STATUS_LABELS = { pending: 'Pending', received: 'Received', returned: 'Returned', approved: 'Approved' }
export const REGISTER_ITEM_TYPES = ['Shop Drawings', 'Product Data', 'Sample', 'Other']

// ── As-of viewer (Round 12.5 — drawing-sheet versioning model) ────────────────
// (issueSpecAtMilestone removed — AlderSPEC Phase 0: the Round-12.5 project-wide issue action is
// gone; issuing happens per-milestone via issueMilestoneSpec when the milestone posts.)

/** The spec as-of a date: latest version snapshot per section issued on or before asOfDate. */
export function getSpecAtMilestone(projectId, asOfDate) {
  const qs = 'action=specAtMilestone&projectId=' + encodeURIComponent(projectId) +
    (asOfDate ? '&asOfDate=' + encodeURIComponent(asOfDate) : '')
  return call('GET', null, qs).then(d => d.sections || [])
}

/**
 * Submittal Review Tool Phase 4 — the Review Workspace's Specs pane. Dual-mode, mirroring
 * api/contractor-portal.js's buildSpecs/buildLegacySpecs fallback:
 *   { mode: 'sections', sections: [{ id, sectionNumber, sectionName, latestVersion: { id, pdfUrl, releaseMode, issuedDate } }] }
 *   { mode: 'legacy', pdf: {url, filename} | null, versionNumber?, postDate?, history?: [{ version, postDate, pdf }] }
 */
export function getReviewSpecs(projectId) {
  if (!projectId) return Promise.resolve({ mode: 'legacy', pdf: null })
  return call('GET', null, 'action=reviewSpecs&projectId=' + encodeURIComponent(projectId))
}

// (The Round-14 "release target" wrappers — getSpecReleases / openSpecTarget / cancelSpecTarget /
// markSpecSectionDone / releaseSpecTarget — were removed in AlderSPEC Phase 0 along with their API
// actions and the spec_milestone_releases table. Per-section milestone membership below is the model.)

// ── Milestone membership (Round 18 — the drawing-sheet model) ─────────────────

/** Sections that belong to a milestone (have a version there). Each row = section fields + `version`
 *  (the version row for this milestone). Editable when `version.status === 'open'`. */
export function getMilestoneSpecSections(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve([])
  return call('GET', null, 'action=milestoneSections&milestoneId=' + encodeURIComponent(milestoneAirtableId))
    .then(d => d.sections || [])
}

/** Sections at a milestone whose body changed from the prior issued snapshot but whose change note is
 *  still blank/default. Drives the pre-post nudge. Returns [{ section_number, section_name }]. */
export function getMilestoneUnnotedChanges(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve([])
  return call('GET', null, 'action=milestoneUnnotedChanges&milestoneId=' + encodeURIComponent(milestoneAirtableId))
    .then(d => d.sections || [])
}

/** Every project (concept) section with its full version chain, for the Reference browse + orphan flag.
 *  A section with `versions.length === 0` is an orphan (not assigned to any milestone). */
export function getSpecSectionsWithVersions(projectId, conceptAirtableId) {
  const qs = conceptAirtableId
    ? 'action=sectionsWithVersions&conceptAirtableId=' + encodeURIComponent(conceptAirtableId)
    : 'action=sectionsWithVersions&projectId=' + encodeURIComponent(projectId)
  return call('GET', null, qs).then(d => d.specSections || [])
}

/** Create a NEW section directly in a milestone (Original Release open version). */
export function createSectionInMilestone(projectId, conceptAirtableId, milestone, fields) {
  return call('POST', {
    action: 'createSectionInMilestone', projectId, conceptAirtableId: conceptAirtableId || null,
    milestoneAirtableId: milestone.id, milestoneName: milestone.name || null, fields,
  }).then(d => ({ specSection: d.specSection, version: d.version }))
}

/** Add an existing section to a milestone (pull forward / assign orphan). Creates an open version. */
export function addSectionToMilestone(specSectionId, milestone, revisionType) {
  return call('POST', {
    action: 'addSectionToMilestone', specSectionId,
    milestoneAirtableId: milestone.id, milestoneName: milestone.name || null,
    revisionType: revisionType || 'Carried Forward',
  }).then(d => ({ version: d.version, already: d.already }))
}

/** Remove a section from a milestone (undo a pull / un-assign). Only OPEN versions. */
export function removeSectionFromMilestone(versionId) {
  return call('POST', { action: 'removeSectionFromMilestone', versionId }).then(d => d.ok)
}

/** Issue a milestone's spec — freeze every open version at the milestone into a snapshot. */
export function issueMilestoneSpec(projectId, conceptAirtableId, milestone, opts = {}) {
  return call('POST', {
    action: 'issueMilestoneSpec', projectId, conceptAirtableId: conceptAirtableId || null,
    milestoneAirtableId: milestone.id, milestoneName: milestone.name || null,
    mode: milestone.mode || 'full', issuedDate: opts.issuedDate || null, changesSummary: opts.changesSummary || null,
  }).then(d => ({ issued: d.issued, sections: d.sections || [] }))
}

// ── Line-level outline (Round 13) ─────────────────────────────────────────────

/** The project's per-line outline for a section (ordered by sort_order). */
export function getSectionLines(specSectionId) {
  if (!specSectionId) return Promise.resolve([])
  return call('GET', null, 'action=sectionLines&specSectionId=' + encodeURIComponent(specSectionId))
    .then(d => d.lines || [])
}

/** Build a draft section's lines from its master template (idempotent). Returns the line rows. */
export function materializeSectionFromTemplate(specSectionId) {
  return call('POST', { action: 'materializeFromTemplate', specSectionId }).then(d => d.lines || [])
}

/** Bulk: ensure every included section is line-materialized in one round-trip.
 *  `sections` = [{ master_section_id, section_number, section_name, division }] (the included set).
 *  Returns { sections: [rows], linesBySection: { [specSectionId]: [lines] } }. */
export function materializeProjectFromTemplate(projectId, conceptAirtableId, sections) {
  return call('POST', { action: 'materializeProjectFromTemplate', projectId, conceptAirtableId, sections })
    .then(d => ({ sections: d.sections || [], linesBySection: d.linesBySection || {} }))
}

/** Batch-save edited line rows (update by id, insert new). Returns the refreshed ordered list. */
export function saveSectionLines(specSectionId, lines) {
  return call('POST', { action: 'saveSectionLines', specSectionId, lines }).then(d => d.lines || [])
}

/** Add a single line below a sort position. */
export function addSectionLine(specSectionId, fields, afterSort) {
  return call('POST', { action: 'addSectionLine', specSectionId, fields, afterSort }).then(d => d.line)
}

/** Delete a single project line. */
export function deleteSectionLine(lineId) {
  return call('POST', { action: 'deleteSectionLine', lineId })
}

/** Pull the latest master template into a draft section (merge: add/drop/refresh, keep project edits).
 *  Resolves to { lines, based_on_template_version, summary: { added, removed, refreshed } }. */
export function pullSpecTemplate(specSectionId) {
  return call('POST', { action: 'pullTemplate', specSectionId })
    .then(d => ({ lines: d.lines || [], based_on_template_version: d.based_on_template_version, summary: d.summary || {} }))
}

// ── Spec sections ─────────────────────────────────────────────────────────────

/**
 * Load spec sections for the APPROVED concept of a project.
 * Pass `conceptAirtableId` directly (preferred); if null or omitted, falls back
 * to a project-level query (all sections regardless of concept).
 */
export function getConceptSpecSections(conceptAirtableId) {
  if (!conceptAirtableId) return Promise.resolve([])
  return call('GET', null, 'conceptAirtableId=' + encodeURIComponent(conceptAirtableId))
    .then(d => d.specSections || [])
}

/** List all spec sections for a project (concept-agnostic fallback). */
export function getProjectSpecSections(projectId) {
  if (!projectId) return Promise.resolve([])
  return call('GET', null, 'projectId=' + encodeURIComponent(projectId))
    .then(d => d.specSections || [])
}

export function getSpecSection(specSectionId) {
  return call('GET', null, 'specSectionId=' + encodeURIComponent(specSectionId))
    .then(d => d.specSection)
}

/**
 * Create a spec section.
 * fields: { section_number, section_name, division?, notes?, status? }
 * division is auto-derived from section_number if omitted.
 * conceptAirtableId: the Airtable Project Concept id (Selected Concept = true).
 */
export function createSpecSection(projectId, fields, conceptAirtableId) {
  return call('POST', { action: 'createSection', projectId, conceptAirtableId: conceptAirtableId || null, fields }).then(d => d.specSection)
}

export function updateSpecSection(specSectionId, fields) {
  return call('POST', { action: 'updateSection', specSectionId, fields }).then(d => d.ok)
}

export function deleteSpecSection(specSectionId) {
  return call('POST', { action: 'deleteSection', specSectionId }).then(d => d.ok)
}

// ── Spec section versions ─────────────────────────────────────────────────────

/** List all versions for a section, ordered by version_number asc. */
export function getSpecSectionVersions(specSectionId) {
  if (!specSectionId) return Promise.resolve([])
  return call('GET', null, 'action=versions&specSectionId=' + encodeURIComponent(specSectionId))
    .then(d => d.versions || [])
}

/**
 * Create a new version (auto-increments version_number, links parent_version_id,
 * updates spec_sections.current_version_id + status → 'issued').
 * fields: { milestone_airtable_id?, milestone_name?, issued_date?, pdf_url?,
 *           pdf_path?, changes_summary? }
 */
export function createSpecSectionVersion(specSectionId, fields) {
  return call('POST', { action: 'createVersion', specSectionId, fields }).then(d => d.version)
}

export function updateSpecSectionVersion(versionId, fields) {
  return call('POST', { action: 'updateVersion', versionId, fields }).then(d => d.ok)
}

/**
 * Spec section versions issued at a specific milestone — the "drawing-sheet
 * parallel": only what was passed through or changed at this milestone.
 * Each row includes the parent spec_sections data (section_number, section_name).
 */
export function getMilestoneSpecVersions(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve([])
  return call('GET', null, 'action=milestoneVersions&milestoneId=' + encodeURIComponent(milestoneAirtableId))
    .then(d => d.versions || [])
}

// ── Submittal register ────────────────────────────────────────────────────────

/** Expected submittal items for a section. */
export function getSpecSectionRegisterItems(specSectionId) {
  if (!specSectionId) return Promise.resolve([])
  return call('GET', null, 'action=register&specSectionId=' + encodeURIComponent(specSectionId))
    .then(d => d.registerItems || [])
}

/** All expected submittal items across the whole project (for the GC portal list). */
export function getProjectRegisterItems(projectId) {
  if (!projectId) return Promise.resolve([])
  return call('GET', null, 'action=register&projectId=' + encodeURIComponent(projectId))
    .then(d => d.registerItems || [])
}

/**
 * Create an expected submittal item on the register.
 * fields: { type?, description?, required_by? }
 */
export function createRegisterItem(specSectionId, projectId, fields) {
  return call('POST', { action: 'createRegisterItem', specSectionId, projectId, fields }).then(d => d.item)
}

export function updateRegisterItem(itemId, fields) {
  return call('POST', { action: 'updateRegisterItem', itemId, fields }).then(d => d.ok)
}

export function deleteRegisterItem(itemId) {
  return call('POST', { action: 'deleteRegisterItem', itemId }).then(d => d.ok)
}

/**
 * Link an actual submittal to a register item and mark it received.
 * Called when the team creates a submittal and manually links it, or
 * automatically from the GC portal flow (Session 6).
 */
export function fulfillRegisterItem(itemId, submittalId) {
  return call('POST', { action: 'fulfillRegisterItem', itemId, submittalId }).then(d => d.ok)
}

// ── Changes since the last release, edit history, restore (docs/ALDERSPEC.md
//    "Changes, narratives, history and restore") ──────────────────────────────────────────────

const scopeQs = (projectId, conceptAirtableId) =>
  'projectId=' + encodeURIComponent(projectId) + (conceptAirtableId ? '&conceptAirtableId=' + encodeURIComponent(conceptAirtableId) : '')

/** The working spec against the last release of a kind ('body' | 'outline'), plus schedule items.
 *  → { baseline, sections:[{ id, section_number, section_name, status, before, after, stats, phrase }],
 *      unchanged, schedules:[{ id, schedule_type, label, status, fields }], drafted }
 *  With milestoneId: compared for posting that milestone (its own members only). */
export function getSpecChanges(projectId, conceptAirtableId, { kind = 'body', milestoneId = null } = {}) {
  return call('GET', null, 'action=changes&' + scopeQs(projectId, conceptAirtableId) + '&kind=' + kind +
    (milestoneId ? '&milestoneId=' + encodeURIComponent(milestoneId) : ''))
}

/** Edit-history checkpoints, newest first (optionally one target: a section id, an item id). */
export function getSpecHistory(projectId, conceptAirtableId, targetId) {
  return call('GET', null, 'action=history&' + scopeQs(projectId, conceptAirtableId) +
    (targetId ? '&targetId=' + encodeURIComponent(targetId) : '')).then(d => d.history || [])
}

/** What restoring a checkpoint would change, against the working spec today. */
export function getSpecHistoryPreview(historyId) {
  return call('GET', null, 'action=historyPreview&id=' + encodeURIComponent(historyId)).then(d => d.preview)
}

/** Put a checkpoint back into the working spec (itself undoable). */
export function restoreSpecHistory(historyId) {
  return call('POST', { action: 'restoreHistory', historyId })
}

/** Put the whole working spec back to how it stood when a release posted. */
export function restoreSpecRelease(milestoneId) {
  return call('POST', { action: 'restoreRelease', milestoneId })
}

/** Posted narratives + which releases can be restored → { notes:[...], restorable:[milestoneId] }. */
export function getSpecReleaseRecords(projectId, conceptAirtableId) {
  return call('GET', null, 'action=releaseRecords&' + scopeQs(projectId, conceptAirtableId))
}

/** Store the narrative posted with a release. */
export function saveSpecReleaseNotes(fields) {
  return call('POST', { action: 'saveReleaseNotes', ...fields })
}
